import type { BaseQueryFn, TagDescription } from "@reduxjs/toolkit/query";

import { apiSlice } from "./http";
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from "../constants/pagination";
import { Tags } from "../enum/tags";
import { toQueryParams } from "../utils/to-query-params";
import type {
  IAddPro014ItemsRequest,
  IAddPro014ItemsResponse,
  IBulkUpdatePro014ItemsRequest,
  IBulkUpdatePro014ItemsResponse,
  ICreatePro014CampaignRequest,
  ICreatePro014CampaignResponse,
  IPro014CampaignDetail,
  IPro014CampaignItem,
  IPro014CreationJobStatus,
  IPro014EditSnapshot,
  IPro014ItemRef,
  IPro014ItemsPage,
  IPro014ItemsPageParams,
  IPro014ItemsSummary,
  IPro014Message,
  IPro014StoreItemRef,
  IUpdatePro014CampaignRequest,
  IUpdatePro014CampaignResponse,
  IUpdatePro014GradeRestrictionRequest,
  IUpdatePro014GradeRestrictionResponse,
  IUpdatePro014StoresRequest,
  IUpdatePro014StoresResponse,
} from "@/@types/campaign";
import {
  PRO014_ITEMS_MAX_LIMIT,
  PRO014_ITEM_SORT,
  PRO014_ITEM_STATUS_FILTER,
} from "@/@types/campaign";
import type {
  IConflictResolution,
  IValidateConflictsResponse,
  IValidatePro014ConflictsRequest,
  ResolveConflictResult,
} from "@/@types/campaign-conflict";
import type {
  IGradeCatalog,
  IItemGradeHistory,
  IItemGradeTargetsResponse,
  IReplaceGradeTargetsRequest,
  IReplaceGradeTargetsResponse,
  IValidateGradeTargetsRequest,
  IValidateGradeTargetsResponse,
} from "@/@types/campaign-grade";

const PRO014_BASE = "/campaigns/pro014";
const GRADE_BASE = "/campaigns/grade";
const GRADE_CATALOG_CACHE_SECONDS = 43200;
const GRADE_HISTORY_CACHE_SECONDS = 300;

const campaignTag = (id: string): TagDescription<Tags> => ({ type: Tags.CAMPAIGN, id });

const PRO014_TAG = {
  detail: (code: number): TagDescription<Tags> => campaignTag(`pro014-${code}`),
  unified: (code: number): TagDescription<Tags> => campaignTag(`PRO014-${code}`),
  audit: (code: number): TagDescription<Tags> => campaignTag(`audit-PRO014-${code}`),
  stores: (code: number): TagDescription<Tags> => campaignTag(`pro014-stores-${code}`),
  items: (code: number): TagDescription<Tags> => campaignTag(`pro014-items-${code}`),
  itemsSummary: (code: number): TagDescription<Tags> =>
    campaignTag(`pro014-items-summary-${code}`),
  itemsPaginated: (code: number): TagDescription<Tags> =>
    campaignTag(`pro014-items-paginated-${code}`),
  grade: (code: number): TagDescription<Tags> => campaignTag(`pro014-grade-${code}`),
  editSnapshot: (code: number): TagDescription<Tags> => campaignTag(`pro014-edit-${code}`),
  creationJob: (jobId: string | number): TagDescription<Tags> =>
    campaignTag(`pro014-create-job-${jobId}`),
};

const itemTags = (code: number): TagDescription<Tags>[] => [
  PRO014_TAG.detail(code),
  PRO014_TAG.items(code),
  PRO014_TAG.itemsSummary(code),
  PRO014_TAG.itemsPaginated(code),
];

const campaignTags = (code: number): TagDescription<Tags>[] => [
  Tags.CAMPAIGNS,
  PRO014_TAG.detail(code),
  PRO014_TAG.unified(code),
  PRO014_TAG.audit(code),
];

const encodeItem = (itemCode: string): string => encodeURIComponent(itemCode);

const pro014ItemsUrl = ({
  code,
  page = DEFAULT_PAGE,
  limit = DEFAULT_PER_PAGE,
  status = PRO014_ITEM_STATUS_FILTER.ALL,
  search,
  sort = PRO014_ITEM_SORT.CODE_ASC,
  storeCode,
}: IPro014ItemsPageParams): string =>
  `${PRO014_BASE}/${code}/items?${toQueryParams({ page, limit, status, sort, search, storeCode })}`;

const unwrapItemsPage = (
  response: IPro014ItemsPage | { data: IPro014ItemsPage },
): IPro014ItemsPage => ("data" in response ? response.data : response);

type Pro014BaseQuery = (args: { url: string; method: string }) => ReturnType<BaseQueryFn>;

const loadAllPro014Items = async (
  code: number,
  baseQuery: Pro014BaseQuery,
): Promise<{ data: IPro014CampaignItem[] } | { error: unknown }> => {
  const items: IPro014CampaignItem[] = [];
  let page: number = DEFAULT_PAGE;
  let totalPages: number = DEFAULT_PAGE;
  while (page <= totalPages) {
    const result: Awaited<ReturnType<Pro014BaseQuery>> = await baseQuery({
      url: pro014ItemsUrl({ code, page, limit: PRO014_ITEMS_MAX_LIMIT }),
      method: "GET",
    });
    if (result.error) return { error: result.error };
    const itemsPage: IPro014ItemsPage = unwrapItemsPage(
      result.data as IPro014ItemsPage | { data: IPro014ItemsPage },
    );
    items.push(...itemsPage.items);
    totalPages = itemsPage.totalPages;
    page += 1;
  }
  return { data: items };
};

export const campaignPro014Service = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createPro014Campaign: builder.mutation<
      ICreatePro014CampaignResponse,
      ICreatePro014CampaignRequest
    >({
      query: (body) => ({ url: PRO014_BASE, method: "POST", body }),
      invalidatesTags: [Tags.CAMPAIGNS],
    }),

    getPro014CreationJobStatus: builder.query<IPro014CreationJobStatus, string | number>({
      query: (jobId) => ({ url: `${PRO014_BASE}/creation-jobs/${jobId}`, method: "GET" }),
      providesTags: (_result, _error, jobId) => [PRO014_TAG.creationJob(jobId)],
    }),

    validatePro014Conflicts: builder.mutation<
      IValidateConflictsResponse,
      IValidatePro014ConflictsRequest
    >({
      query: (body) => ({ url: `${PRO014_BASE}/conflicts/validate`, method: "POST", body }),
    }),

    resolveConflictItem: builder.mutation<ResolveConflictResult, IConflictResolution>({
      query: (body) => ({ url: "/campaigns/conflicts/resolve-item", method: "POST", body }),
      invalidatesTags: [Tags.CAMPAIGNS],
    }),

    getPro014CampaignByCode: builder.query<IPro014CampaignDetail, number>({
      query: (code) => ({ url: `${PRO014_BASE}/${code}`, method: "GET" }),
      providesTags: (_result, _error, code) => [PRO014_TAG.detail(code)],
    }),

    updatePro014Campaign: builder.mutation<
      IUpdatePro014CampaignResponse,
      IUpdatePro014CampaignRequest
    >({
      query: ({ code, ...body }) => ({ url: `${PRO014_BASE}/${code}`, method: "PUT", body }),
      invalidatesTags: (_result, _error, { code }) => campaignTags(code),
    }),

    updatePro014CampaignStores: builder.mutation<
      IUpdatePro014StoresResponse,
      IUpdatePro014StoresRequest
    >({
      query: ({ code, ...body }) => ({
        url: `${PRO014_BASE}/${code}/stores`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { code }) => [
        ...campaignTags(code),
        PRO014_TAG.stores(code),
        PRO014_TAG.itemsSummary(code),
        PRO014_TAG.itemsPaginated(code),
        PRO014_TAG.editSnapshot(code),
      ],
    }),

    updatePro014GradeRestriction: builder.mutation<
      IUpdatePro014GradeRestrictionResponse,
      IUpdatePro014GradeRestrictionRequest
    >({
      query: ({ code, enabled }) => ({
        url: `${PRO014_BASE}/${code}/grade-restriction`,
        method: "PATCH",
        body: { enabled },
      }),
      invalidatesTags: (_result, _error, { code }) => [
        ...campaignTags(code),
        PRO014_TAG.grade(code),
        PRO014_TAG.itemsPaginated(code),
      ],
    }),

    addPro014CampaignItems: builder.mutation<IAddPro014ItemsResponse, IAddPro014ItemsRequest>({
      query: ({ code, itemCodes, gradeTargets }) => ({
        url: `${PRO014_BASE}/${code}/items`,
        method: "POST",
        body: { itemCodes, gradeTargets },
      }),
      invalidatesTags: (_result, _error, { code }) => [
        ...itemTags(code),
        PRO014_TAG.unified(code),
        PRO014_TAG.grade(code),
      ],
    }),

    activatePro014Item: builder.mutation<IPro014Message, IPro014ItemRef>({
      query: ({ code, itemCode }) => ({
        url: `${PRO014_BASE}/${code}/items/${encodeItem(itemCode)}/activate`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { code }) => [...itemTags(code), PRO014_TAG.unified(code)],
    }),

    deactivatePro014Item: builder.mutation<IPro014Message, IPro014ItemRef>({
      query: ({ code, itemCode }) => ({
        url: `${PRO014_BASE}/${code}/items/${encodeItem(itemCode)}/deactivate`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { code }) => [...itemTags(code), PRO014_TAG.unified(code)],
    }),

    activatePro014CampaignItem: builder.mutation<IPro014Message, IPro014StoreItemRef>({
      query: ({ code, itemCode, storeCode }) => ({
        url: `${PRO014_BASE}/${code}/items/${encodeItem(itemCode)}/${storeCode}/activate`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { code }) => itemTags(code),
    }),

    deactivatePro014CampaignItem: builder.mutation<IPro014Message, IPro014StoreItemRef>({
      query: ({ code, itemCode, storeCode }) => ({
        url: `${PRO014_BASE}/${code}/items/${encodeItem(itemCode)}/${storeCode}/deactivate`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { code }) => itemTags(code),
    }),

    getPro014ItemsPaginated: builder.query<IPro014ItemsPage, IPro014ItemsPageParams>({
      query: (params) => ({ url: pro014ItemsUrl(params), method: "GET" }),
      transformResponse: unwrapItemsPage,
      providesTags: (_result, _error, { code }) => [PRO014_TAG.itemsPaginated(code)],
    }),

    getPro014EditSnapshot: builder.query<IPro014EditSnapshot, number>({
      queryFn: async (code, _api, _extraOptions, baseQuery) => {
        const detail: Awaited<ReturnType<typeof baseQuery>> = await baseQuery({
          url: `${PRO014_BASE}/${code}`,
          method: "GET",
        });
        if (detail.error) return { error: detail.error };
        const items: { data: IPro014CampaignItem[] } | { error: unknown } =
          await loadAllPro014Items(code, baseQuery);
        if ("error" in items) return { error: items.error };
        return { data: { detail: detail.data as IPro014CampaignDetail, items: items.data } };
      },
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, code) => [PRO014_TAG.editSnapshot(code)],
    }),

    getPro014AllItems: builder.query<IPro014CampaignItem[], number>({
      queryFn: (code, _api, _extraOptions, baseQuery) => loadAllPro014Items(code, baseQuery),
      providesTags: (_result, _error, code) => [PRO014_TAG.itemsPaginated(code)],
    }),

    getPro014ItemsSummary: builder.query<IPro014ItemsSummary, number>({
      query: (code) => ({ url: `${PRO014_BASE}/${code}/items/summary`, method: "GET" }),
      providesTags: (_result, _error, code) => [PRO014_TAG.itemsSummary(code)],
    }),

    bulkUpdatePro014Items: builder.mutation<
      IBulkUpdatePro014ItemsResponse,
      IBulkUpdatePro014ItemsRequest
    >({
      query: ({ code, items, action }) => ({
        url: `${PRO014_BASE}/${code}/items/bulk`,
        method: "PATCH",
        body: { items, action },
      }),
      invalidatesTags: (_result, _error, { code }) => itemTags(code),
    }),

    getPro014AvailableStores: builder.query<number[], number>({
      query: (code) => ({ url: `${PRO014_BASE}/${code}/stores`, method: "GET" }),
      providesTags: (_result, _error, code) => [PRO014_TAG.stores(code)],
    }),

    getGradeCatalog: builder.query<IGradeCatalog, void>({
      query: () => ({ url: `${GRADE_BASE}/catalog`, method: "GET" }),
      keepUnusedDataFor: GRADE_CATALOG_CACHE_SECONDS,
    }),

    getItemGradeHistory: builder.query<IItemGradeHistory, string>({
      query: (itemCode) => ({
        url: `${GRADE_BASE}/items/${encodeItem(itemCode)}/sold`,
        method: "GET",
      }),
      keepUnusedDataFor: GRADE_HISTORY_CACHE_SECONDS,
    }),

    getItemGradeTargets: builder.query<IItemGradeTargetsResponse, IPro014ItemRef>({
      query: ({ code, itemCode }) => ({
        url: `${GRADE_BASE}/targets/${code}/${encodeItem(itemCode)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { code }) => [PRO014_TAG.grade(code)],
    }),

    replaceItemGradeTargets: builder.mutation<
      IReplaceGradeTargetsResponse,
      IReplaceGradeTargetsRequest
    >({
      query: ({ code, itemCode, targets }) => ({
        url: `${GRADE_BASE}/targets/${code}/${encodeItem(itemCode)}`,
        method: "PUT",
        body: { targets },
      }),
      invalidatesTags: (_result, _error, { code }) => [
        PRO014_TAG.grade(code),
        PRO014_TAG.itemsPaginated(code),
      ],
    }),

    validateGradeTargets: builder.mutation<
      IValidateGradeTargetsResponse,
      IValidateGradeTargetsRequest
    >({
      query: (body) => ({ url: `${GRADE_BASE}/validate`, method: "POST", body }),
    }),
  }),
  overrideExisting: true,
});

export const invalidatePro014EditSnapshot = (
  code: number,
): ReturnType<typeof campaignPro014Service.util.invalidateTags> =>
  campaignPro014Service.util.invalidateTags([PRO014_TAG.editSnapshot(code)]);

export const {
  useCreatePro014CampaignMutation,
  useGetPro014CreationJobStatusQuery,
  useValidatePro014ConflictsMutation,
  useResolveConflictItemMutation,
  useGetPro014CampaignByCodeQuery,
  useUpdatePro014CampaignMutation,
  useUpdatePro014CampaignStoresMutation,
  useUpdatePro014GradeRestrictionMutation,
  useAddPro014CampaignItemsMutation,
  useActivatePro014ItemMutation,
  useDeactivatePro014ItemMutation,
  useActivatePro014CampaignItemMutation,
  useDeactivatePro014CampaignItemMutation,
  useGetPro014ItemsPaginatedQuery,
  useGetPro014EditSnapshotQuery,
  useGetPro014AllItemsQuery,
  useGetPro014ItemsSummaryQuery,
  useBulkUpdatePro014ItemsMutation,
  useGetPro014AvailableStoresQuery,
  useGetGradeCatalogQuery,
  useGetItemGradeHistoryQuery,
  useGetItemGradeTargetsQuery,
  useReplaceItemGradeTargetsMutation,
  useValidateGradeTargetsMutation,
} = campaignPro014Service;
